import styled, { css, keyframes } from 'styled-components'
import query from 'syf-component-library/const/mediaQueries'

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

type TypographyToken =
  | 'label-large'
  | 'label-large-emphasized'
  | 'label-medium'
  | 'label-small'
  | 'title-large-emphasized'

const typographyType = (
  baseToken: TypographyToken,
  rhythmToken: TypographyToken = baseToken
) => css`
  font-family: var(--syfds-typography-${baseToken}-font-family);
  font-size: var(--syfds-typography-${baseToken}-font-size);
  font-weight: var(--syfds-typography-${baseToken}-font-weight);
  line-height: var(--syfds-typography-${rhythmToken}-line-height);
  letter-spacing: var(--syfds-typography-${rhythmToken}-letter-spacing);
`

const labelLargeType = typographyType('label-large', 'label-large-emphasized')
const titleLargeEmphasizedType = typographyType('title-large-emphasized')
const labelMediumType = typographyType('label-medium')
const labelSmallType = typographyType('label-small')

const SEARCH_WIDTH_DEFAULT = '344px'
const SEARCH_WIDTH_COMPACT = '240px' // tablet and mobile: tighter header space

export const SearchContainer = styled.div<{
  $isExpanded: boolean
  $isPortaled: boolean
}>`
  ${({ $isPortaled, $isExpanded }) =>
    $isPortaled
      ? css`
          position: relative;
          display: flex;
          flex-shrink: 0;
          align-items: center;
          margin-right: 8px;
          z-index: 1;

          @media ${query.lessThanMedium} {
            /* Shrinks into leftover mobile header space. */
            flex: 0 1 auto;
            width: ${$isExpanded ? SEARCH_WIDTH_COMPACT : '40px'};
            min-width: 40px;
            transform: none;
            transition: width 0.2s ease;
          }
        `
      : css`
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          z-index: 1;

          @media ${query.lessThanMedium} {
            left: auto;
            right: 64px;
            transform: translateY(-50%);
            width: ${$isExpanded
              ? `min(${SEARCH_WIDTH_COMPACT}, calc(100% - 128px))`
              : '40px'};
            transition: width 0.2s ease;
            z-index: 1;
          }
        `}
`

export const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: ${SEARCH_WIDTH_DEFAULT};
  max-height: 480px;
  overflow-y: auto;
  background: var(--syfds-color-background-brand-subtle-default);
  border-radius: var(--syfds-border-radius-2xlg);
  z-index: 1000;
  animation: ${slideDown} 0.15s ease-out;
  padding: var(--syfds-spacing-dropdown-menu-horiz-padding-lg);
  scrollbar-width: thin;
  scrollbar-color: var(--syfds-color-border-default) transparent;

  @media ${query.betweenMediumLarge} {
    width: ${SEARCH_WIDTH_COMPACT};
  }

  @media ${query.lessThanMedium} {
    width: 100%;
    margin: 0;
  }
`

export const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  @media ${query.lessThanMedium} {
    min-width: 0;
  }
`

export const SearchInput = styled.input<{ $isExpanded: boolean }>`
  ${labelLargeType}
  width: ${SEARCH_WIDTH_DEFAULT};
  height: 48px;
  padding: 12px 80px 12px var(--syfds-spacing-dropdown-menu-horiz-padding-lg);
  border: var(--syfds-border-width-sm) solid var(--syfds-color-border-knockout);
  border-radius: var(--syfds-border-radius-round);
  background: var(--syfds-color-background-brand-subtle-default);
  color: var(--syfds-color-content-subtle-default);
  outline: none;
  transition: border 0.2s ease, background 0.2s ease, color 0.2s ease;

  &::placeholder {
    color: var(--syfds-color-content-subtle-default);
  }

  &:hover {
    border-color: var(--syfds-color-border-hover);
    background: var(--syfds-color-background-brand-subtle-hover);
    color: var(--syfds-color-content-subtle-hover);
  }

  &:focus,
  &:not(:placeholder-shown) {
    border-color: var(--syfds-color-border-active);
    border-width: var(--syfds-border-width-md);
    background: var(--syfds-color-background-brand-subtle-active);
    color: var(--syfds-color-content-default);
  }

  @media ${query.betweenMediumLarge} {
    width: ${SEARCH_WIDTH_COMPACT};
  }

  @media ${query.lessThanMedium} {
    font-size: 16px;
    width: 100%;
    min-width: 0;
    height: 40px;
    padding: 0;
    border: none;
    color: transparent;
    caret-color: transparent;

    &::placeholder {
      color: transparent;
    }

    &:focus-visible {
      outline: var(--syfds-focus-ring-border-width-default) solid
        var(--syfds-focus-ring-color-border-default);
      outline-offset: 4px;
    }

    ${({ $isExpanded }) =>
      $isExpanded &&
      css`
        padding: 8px 80px 8px
          var(--syfds-spacing-dropdown-menu-horiz-padding-lg);
        border: var(--syfds-border-width-md) solid
          var(--syfds-color-border-active);
        background: var(--syfds-color-background-brand-subtle-active);
        color: var(--syfds-color-content-default);
        caret-color: auto;

        &::placeholder {
          color: var(--syfds-color-content-subtle-default);
        }
      `}
  }
`

export const SearchIcon = styled.span`
  position: absolute;
  right: var(--syfds-spacing-dropdown-menu-horiz-padding-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--syfds-color-content-default);
  pointer-events: none;

  @media ${query.lessThanMedium} {
    right: 8px;
  }
`

export const ClearButton = styled.button`
  position: absolute;
  right: calc(var(--syfds-spacing-dropdown-menu-horiz-padding-lg) + 32px);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--syfds-border-radius-round);
  cursor: pointer;
  color: var(--syfds-color-content-default);
  font-size: 12px;
  transition: background 0.15s;

  &:hover {
    background: var(--syfds-color-background-brand-subtle-active);
  }
`

export const CategoryHeader = styled.div`
  ${titleLargeEmphasizedType}
  color: var(--syfds-color-content-default);
  padding-bottom: 8px;
`

export const AccountPickerHeader = CategoryHeader

export const ResultsList = styled.div`
  display: flex;
  flex-direction: column;

  > div:not(:first-child) ${CategoryHeader} {
    margin-top: 24px;
  }
`

const selectableItemStyles = css<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  padding: 4px 8px;
  border: 1px solid
    ${({ $isActive }) =>
      $isActive ? 'var(--syfds-color-border-hover)' : 'transparent'};
  border-radius: var(--syfds-border-radius-lg);
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: all 0.12s;

  &:hover {
    border-color: var(--syfds-color-border-hover);
  }
`

export const ResultItem = styled.button<{ $isActive?: boolean }>`
  ${selectableItemStyles}
  ${labelMediumType}
  color: var(--syfds-color-content-brand-default);
`

export const ResultLabel = styled.span`
  ${labelMediumType}
  color: var(--syfds-color-content-brand-default);
`

export const AccountName = ResultLabel

export const AccountPickerContainer = styled.div`
  display: flex;
  flex-direction: column;
`

export const AccountItem = styled.button<{ $isActive?: boolean }>`
  ${selectableItemStyles}
`

export const AccountDetails = styled.div`
  display: flex;
  flex-direction: column;
`

export const AccountLast4 = styled.span`
  ${labelSmallType}
  color: var(--syfds-color-content-brand-default);
  font-variant-numeric: tabular-nums;
`

export const EmptyState = styled.div`
  ${labelMediumType}
  padding: 8px;
  color: var(--syfds-color-content-default);
`
